<template>
    <section
        class="freelance-search-slice"
        :class="{'freelance-search-slice--hide-in-mobile': slice.primary.hideInMobile}"
        :aria-label="slice.primary.accessibilityLabel || 'Freelance Search'"
    >
        <PrismicRichText :field="slice.primary.title" class="title" />
        <div class="search">
            <SearchPanel
                :search-tab="searchLinkText"
                :search-field-category="slice.primary.categoryLabel"
                :search-field-location="slice.primary.locationLabel"
                :search-submit="searchLinkText"
                :search-submit-link="slice.primary.searchLink"
                :ai-search-tab="slice.primary.aiSearchLinkText"
                :ai-search-tab-link="slice.primary.aiSearchLink"
                :ai-entity-detection-enabled="$ff('one-entry-point-improvements-unlogged-homepage')"
                :reassurance-items="slice.items"
                brief-origin="home_unlogged"
                page-source="home_unlogged_try_ai_search"
                origin="USER_PROMPT_HOMEPAGE_UNLOGGED"
            />
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, onMounted} from 'vue';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {SearchSliceSlice} from '@prismicio-types';
    import SearchPanel from '@search/components/SearchPanel.vue';
    import {useTranslation} from '#imports';
    import {useABTest} from '@malt/feature-flags';
    import errorHelpers from '~/composables/errorHelpers';

    const props = defineProps(getSliceComponentProps<SearchSliceSlice>());

    const {t} = useTranslation();
    const {getVariation} = useABTest();

    const searchLinkText = computed(() =>
        getVariation('keyword-search-cta-label-change') === 'treatment'
            ? t('search.advanced.ai.entrypoint.mode.keyword.search.switch.button.browse')
            : props.slice.primary.searchLinkText,
    );

    onMounted(() => {
        const {getPreviewErrorHandler, throwIfPrimaryPropertiesAreInvalid} = errorHelpers();
        const errorHandler = getPreviewErrorHandler();
        if (errorHandler.enabled) {
            throwIfPrimaryPropertiesAreInvalid(
                errorHandler.toast,
                props.slice.primary,
                [
                    {name: 'title', type: 'field'},
                    {name: 'accessibilityLabel', type: 'rawText'},
                ],
                'search',
            );
        }
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .freelance-search-slice {
        margin: 0 $margin-section-normal 0 $margin-section-normal;
        @include flex-column-align-center;

        &--hide-in-mobile {
            display: none;
            @include mq.screen_sm(min) {
                display: flex;
            }
        }
        @media (max-width: $mobile-breakpoint) {
            margin: 0 $margin-section-mobile 0 $margin-section-mobile;
        }

        .title h2 {
            text-align: center;
            @include secondary-title;
        }

        .search {
            margin: 48px 70px 0 70px;
            width: 100%;
            max-width: 1280px;
            margin-left: auto;
            margin-right: auto;

            @media (max-width: $mobile-breakpoint) {
                margin: 48px 0 0 0;
                width: 100%;
            }
        }
    }
</style>
